import CryptoJS from "crypto-js";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

import Bugsnag from "@bugsnag/js";

export const notifyBugsnagError = (
  error: Error | string,
  metadata?: Record<string, any>
) => {
  Bugsnag.notify(error, (event) => {
    if (metadata) {
      event.addMetadata("custom", metadata);
    }
  });
};

/** Minimum number of characters required before a search-driven API call is made. */
export const MIN_SEARCH_LENGTH = 3;

/**
 * Decides whether a search term should trigger an API call.
 * - Empty search (after trimming) -> true, so clearing the box resets to the full list.
 * - 1 to MIN_SEARCH_LENGTH-1 chars -> false, so partial input holds the current list.
 * - MIN_SEARCH_LENGTH or more chars -> true.
 */
export const shouldTriggerSearch = (searchValue: string | undefined | null) => {
  const trimmed = (searchValue ?? "").trim();
  return trimmed.length === 0 || trimmed.length >= MIN_SEARCH_LENGTH;
};

export const isValidUUID = (uuid: string | undefined) => {
  return typeof uuid === "string" && uuid.trim() !== "" && uuid !== "undefined";
};

/** Default Tipping View option "Simple" (Customize / org list). */
export const DEFAULT_TIPPING_VIEW_SIMPLE = 0;

/** Default Tipping View option "Disabled" (Customize / org list). */
export const DEFAULT_TIPPING_VIEW_DISABLED = 2;

export const isDefaultTippingViewDisabled = (tippingView: unknown) =>
  Number(tippingView) === DEFAULT_TIPPING_VIEW_DISABLED;

/** Org setting: processing fees paid by organization (admin option value 2). */
export const FEE_PAID_BY_ORGANIZATION = 2;

/** Blank fee paid by matches Organization: fees come out of the tip. */
export const isFeePaidByOrganization = (feePaidBy: unknown) => {
  if (feePaidBy == null || feePaidBy === "") {
    return true;
  }

  return Number(feePaidBy) === FEE_PAID_BY_ORGANIZATION;
};

export const getPropertyScoreboardUrl = (propertyUuid?: string) => {
  if (!propertyUuid) {
    return "";
  }

  const base = (
    process.env.REACT_APP_BASE_URL_SCOREBOARD || window.location.origin
  ).replace(/\/$/, "");

  return `${base}/public/${propertyUuid}`;
};

/** get data on the basis of condition */
export const getCurrentUserData = (uuid: string) => {
  const isAdminAsOrg =
    window.location.href.includes("mydashboard") && isValidUUID(uuid);
  if (isAdminAsOrg) {
    return getOrgLocalUserData(uuid);
  } else {
    return getLocalUserData();
  }
};

/** Handle local storage data for organization  */
export const setOrgLocalUserData = (uuid: string, data: string) => {
  localStorage.setItem(uuid + "_org_data", Encrypt(parseJwt(data)));
  localStorage.setItem(uuid, Encrypt(data));
};

/** Handle local storage data */
export const setLocalUserData = (data: string) => {
  localStorage.setItem("tip_userData", Encrypt(parseJwt(data)));
  localStorage.setItem("tip_token", Encrypt(data));
};

export const getLocalUserData = () =>
  localStorage.getItem("tip_userData")
    ? Decrypt(localStorage.getItem("tip_userData"))
    : "";

/** get  local storage data for organization  */
export const getOrgLocalUserData = (uuid: string) => {
  return localStorage.getItem(uuid + "_org_data")
    ? Decrypt(localStorage.getItem(uuid + "_org_data"))
    : "";
};

export const getOrgLocalUserToken = (uuid: string) =>
  localStorage.getItem(uuid) ? Decrypt(localStorage.getItem(uuid) || "") : "";

export const getUserToken = (uuid: string) => {
  const isAdminAsOrg =
    window.location.href.includes("mydashboard") && isValidUUID(uuid);
  if (isAdminAsOrg) {
    return getOrgLocalUserToken(uuid);
  } else {
    return localStorage.getItem("tip_token")
      ? Decrypt(localStorage.getItem("tip_token") || "")
      : "";
  }
};

export const clearUserLocalData = (uuid: string) => {
  const isAdminAsOrg =
    window.location.href.includes("mydashboard") && isValidUUID(uuid as string);
  if (isAdminAsOrg) {
    localStorage.removeItem(uuid);
    localStorage.removeItem(uuid + "_org_data");
  } else {
    localStorage.removeItem("tip_userData");
    localStorage.removeItem("tip_token");
  }
};

/** Encrypt Decrypt Data */
const cryptoSecret = process.env.CRYPTO_KEY ?? "";
export function Encrypt(values: any) {
  const encJson = CryptoJS.AES.encrypt(
    JSON.stringify(values),
    cryptoSecret
  ).toString();
  const encData = CryptoJS.enc.Base64.stringify(
    CryptoJS.enc.Utf8.parse(encJson)
  );
  return encData;
}
export function Decrypt(values: string | null) {
  let bytes = null;
  try {
    const decData = CryptoJS.enc.Base64.parse(
      values == null ? "" : values
    ).toString(CryptoJS.enc.Utf8);
    bytes = CryptoJS.AES.decrypt(decData, cryptoSecret).toString(
      CryptoJS.enc.Utf8
    );
  } catch (e) { }
  return bytes ? JSON.parse(bytes) : {};
}

/** Parse JWT Data */
export const parseJwt = (token: string) => {
  try {
    var base64Url = token.split(".")[1];
    var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    var jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

export const removeInitialSpace = (value: string) => value.replace(/^\s+/g, "");

export function isValidUrl(url: string) {
  if (url && url?.length <= 50) {
    return true;
  } else {
    return false;
  }
}

export function isValidEmail(email: string) {
  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  return emailRegex.test(email);
}

export const copyToClipBoard = (text: string) => {
  if (typeof text === "string") {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        showToast("Copied to clipboard!", "success");
      })
      .catch((err) => {
        showToast("Failed to copy", "error");
      });
  }
};

export const openQrCode = (QrLink: string) => {
  window.open(QrLink, "_blank");
};

export const showToast = (message: string, type: "success" | "error") => {
  if (type === "success") {
    toast.dismiss();
    toast.success(message, { autoClose: 5000 });
  } else if (type === "error") {
    toast.dismiss();
    toast.error(message);
  }
};

const s2ab = (s: string) => {
  const buf = new ArrayBuffer(s.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
  return buf;
};

export const convertToExcel = (tableData: any[]) => {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(tableData);
  // Calculate column widths based on the content length
  const wscols = tableData[0].map((_: any, colIndex: string | number) => ({
    wch: Math.max(
      ...tableData.map((row: { [x: string]: any }) =>
        row[colIndex] ? String(row[colIndex]).length : 0
      )
    ),
  }));
  // Set column widths in the worksheet
  ws["!cols"] = wscols;
  // Add the worksheet to the workbook
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  // Generate a binary string from the workbook
  const wbout = XLSX.write(wb, { type: "binary", bookType: "xlsx" });
  // Convert the binary string to a Blob
  const blob = new Blob([s2ab(wbout)], {
    type: "application/octet-stream",
  });
  // Create a download link
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "jinwen_investors.xlsx");
  // Trigger the download
  document.body.appendChild(link);
  link.click();
  // Clean up
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 0);
};

export const getFrequency = (freq: number | string) => {
  let frequency = "";
  switch (freq) {
    case 1:
      frequency = "Daily";
      break;
    case 2:
      frequency = "Weekly (Monday)";
      break;
    case 3:
      frequency = "Bi-Weekly";
      break;
    case 4:
      frequency = "Monthly";
      break;
    case 5:
      frequency = "Weekly (Sunday)";
      break;
    default:
      break;
  }
  return frequency;
};




const dropdownValues = [{
  name: 'service',
  label: 'Select Service',
  placeholder: 'Select service',
  type: 3
}, {
  name: 'room',
  label: 'Select Room',
  placeholder: 'Select room',
  type: 4
}, {
  name: 'employee',
  label: 'Select Employee',
  placeholder: 'Select employee',
  type: 5
},]
export const getSignMultiSelectDropdown = (type: number | null | undefined) => {
  const response = dropdownValues.filter((item: any) => item.type === type)
  return response
}


export const errorMsgForCommonDropdown = (type: number | null | undefined, isProperty?: boolean) => {
  let message = "not found for selected property.";
  let finalMessage = "";
  if (isProperty && type && type !== 1) {
    finalMessage = "Property not found for this organization.";
  } else {
    switch (type) {
      case 3:
        finalMessage = "Service " + message;
        break;
      case 4:
        finalMessage = "Room " + message;
        break;
      case 5:
        finalMessage = "Employee " + message;
        break;
    }
  }
  return finalMessage;
}

export const requiredErrorMsgForCommonDropdown = (type: number | null | undefined) => {
  let message = "is required.";
  let finalMessage = "";

  switch (type) {
    case 3:
      finalMessage = "Service " + message;
      break;
    case 4:
      finalMessage = "Room " + message;
      break;
    case 5:
      finalMessage = "Employee " + message;
      break;
  }

  return finalMessage;
}


export const isObject = (value: any) => typeof value === 'object' && value !== null && !Array.isArray(value);


export const removeDashed = ''

/**
 * Downloads a PDF file from a URL to the user's system
 * @param pdfUrl - The URL of the PDF to download
 * @param filename - Optional filename for the downloaded PDF (default: "document.pdf")
 */
export const downloadPDF = async (pdfUrl: string, filename: string = "document.pdf") => {
  if (!pdfUrl) {
    showToast("PDF URL not available", "error");
    return;
  }

  try {
    // Fetch the PDF
    const response = await fetch(pdfUrl, {
      method: "GET",
      mode: "cors",
      cache: "no-cache",
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // Convert to blob
    const blob = await response.blob();

    // Verify it's a PDF
    if (!blob.type.includes("pdf") && !pdfUrl.toLowerCase().endsWith(".pdf")) {
      console.warn("File may not be a PDF");
    }

    // Create a download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    link.style.display = "none";

    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);

    // showToast("PDF downloaded successfully", "success");
  } catch (error: any) {
    showToast("Failed to download PDF", "error");
    notifyBugsnagError(error, {
      function: "downloadPDF",
      pdfUrl,
    });
  }
};

/**
 * Prints a PDF file from a URL
 * Fetches the PDF, injects it into a hidden iframe, and triggers print dialog
 * @param pdfUrl - The URL of the PDF to print
 */
export const printPDF = async (pdfUrl: string) => {
  if (!pdfUrl) {
    showToast("PDF URL not available", "error");
    return;
  }

  try {
    const response = await fetch(pdfUrl, {
      method: "GET",
      mode: "cors",
      cache: "no-cache",
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    // Create hidden iframe to host the PDF without opening a new tab
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.src = objectUrl;
    iframe.setAttribute("title", "print-frame");
    document.body.appendChild(iframe);

    const cleanup = () => {
      if (iframe.parentNode) {
        document.body.removeChild(iframe);
      }
      URL.revokeObjectURL(objectUrl);
    };

    const triggerPrint = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();

        // Don't cleanup immediately - keep iframe alive so print dialog stays open
        // Cleanup will happen on page unload or after a very long delay
        // This ensures the print dialog stays open until user closes it

        // Cleanup on page unload
        const handleBeforeUnload = () => {
          cleanup();
          window.removeEventListener('beforeunload', handleBeforeUnload);
        };
        window.addEventListener('beforeunload', handleBeforeUnload);

        // Very long delay cleanup (30 minutes) - only as a safety net for memory leaks
        // This won't interfere with normal printing
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            cleanup();
            window.removeEventListener('beforeunload', handleBeforeUnload);
          }
        }, 1800000); // 30 minutes
      } catch (err) {
        cleanup();
        throw err;
      }
    };

    // Attempt to print when iframe loads
    iframe.onload = () => {
      setTimeout(() => {
        try {
          triggerPrint();
        } catch (e) {
          cleanup();
          showToast("Failed to open print dialog", "error");
          console.error("Print error:", e);
        }
      }, 300);
    };

    // Fallback if onload never triggers
    setTimeout(() => {
      if (document.body.contains(iframe)) {
        try {
          triggerPrint();
        } catch (e) {
          cleanup();
          showToast("Failed to open print dialog", "error");
          console.error("Print error:", e);
        }
      }
    }, 2000);
  } catch (error: any) {
    showToast("Failed to print PDF", "error");
    notifyBugsnagError(error, {
      function: "printPDF",
      pdfUrl,
    });
  }
};



export const formatAmountDisplay = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "";
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  return Number.isInteger(num) ? String(num) : num.toFixed(2);
};

/**
 * Downloads an image from a URL to the user's system
 * Uses multiple fallback approaches to handle CORS and ensure proper download
 * @param imageUrl - The URL of the image to download
 * @param filename - Optional filename for the downloaded image (default: "qr-code.png")
 */
export const downloadImage = async (imageUrl: string, filename: string = "qr-code.png") => {
  if (!imageUrl) {
    showToast("QR code URL not available", "error");
    return;
  }

  // Helper function to trigger download with blob
  const triggerDownload = (blob: Blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    link.style.display = "none";

    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);

    // showToast("QR code downloaded successfully", "success");
  };

  // Method 1: Try XMLHttpRequest (often works better with CORS than fetch)
  try {
    const blob = await new Promise<Blob>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", imageUrl, true);
      xhr.responseType = "blob";

      xhr.onload = () => {
        if (xhr.status === 200 || xhr.status === 0) {
          // Status 0 can occur with file:// or CORS, but response might still be valid
          if (xhr.response instanceof Blob && xhr.response.size > 0) {
            resolve(xhr.response);
          } else {
            reject(new Error("Empty or invalid response"));
          }
        } else {
          reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => {
        reject(new Error("Network error while fetching image"));
      };

      xhr.ontimeout = () => {
        reject(new Error("Request timeout"));
      };

      // Set timeout to 30 seconds
      xhr.timeout = 30000;

      xhr.send();
    });

    triggerDownload(blob);
    return;
  } catch (xhrError: any) {
    // Continue to next method
    console.log("XMLHttpRequest failed, trying next method:", xhrError);
  }

  // Method 2: Try using existing image element from DOM if available
  // This works even with CORS because the image is already loaded in the page
  try {
    // Try multiple selectors to find the image
    let existingImg: HTMLImageElement | null = null;

    // Try exact src match
    existingImg = document.querySelector(`img[src="${imageUrl}"]`) as HTMLImageElement;

    // Try partial match (image filename)
    if (!existingImg) {
      const imageFilename = imageUrl.split('/').pop() || '';
      if (imageFilename) {
        const allImages = document.querySelectorAll('img');
        for (let i = 0; i < allImages.length; i++) {
          if (allImages[i].src.includes(imageFilename) || allImages[i].getAttribute('src')?.includes(imageFilename)) {
            existingImg = allImages[i] as HTMLImageElement;
            break;
          }
        }
      }
    }

    // Try finding by QR code class
    if (!existingImg) {
      existingImg = document.querySelector('img.qrCodeImage, img[class*="qrCode"], img[class*="qr-code"]') as HTMLImageElement;
    }

    if (existingImg && existingImg.complete && existingImg.naturalWidth > 0) {
      const canvas = document.createElement("canvas");
      canvas.width = existingImg.naturalWidth;
      canvas.height = existingImg.naturalHeight;
      const ctx = canvas.getContext("2d");

      if (ctx) {
        ctx.drawImage(existingImg, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) {
            triggerDownload(blob);
            return;
          }
          throw new Error("Failed to convert canvas to blob");
        }, "image/png");
        return;
      }
    }
  } catch (domError: any) {
    console.log("DOM image approach failed, trying next method:", domError);
  }

  // Method 3: Try fetch API
  try {
    const response = await fetch(imageUrl, {
      method: "GET",
      mode: "cors",
      cache: "no-cache",
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const blob = await response.blob();
    triggerDownload(blob);
    return;
  } catch (fetchError: any) {
    console.log("Fetch API failed, trying canvas approach:", fetchError);
  }

  // Method 4: Try canvas with crossOrigin (last resort)
  try {
    const img = new Image();
    img.crossOrigin = "anonymous";

    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error("Image load timeout")), 10000);
      img.onload = () => {
        clearTimeout(timeout);
        resolve();
      };
      img.onerror = () => {
        clearTimeout(timeout);
        reject(new Error("Failed to load image"));
      };
      img.src = imageUrl;
    });

    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("Failed to get canvas context");
    }

    ctx.drawImage(img, 0, 0);

    canvas.toBlob((blob) => {
      if (blob) {
        triggerDownload(blob);
      } else {
        throw new Error("Failed to convert canvas to blob");
      }
    }, "image/png");
    return;
  } catch (canvasError: any) {
    // All methods failed
    showToast("Failed to download QR code. Please try right-clicking the image and selecting 'Save image as...'", "error");
    notifyBugsnagError(canvasError, {
      function: "downloadImage",
      imageUrl,
      message: "All download methods failed",
    });
  }
};

